/**
 * Intake BE(spnp) `/v1/datasets/{uuid}/files` 경계.
 *
 * 서버가 스토어를 실시간으로 읽으므로 **폴더 한 칸**이 한 요청이다. 없는 폴더는 404 가 아니라
 * 빈 목록이고, `..` 처럼 데이터셋 밖을 가리키는 경로는 `400 dataset.source_invalid` 다.
 *
 * Owns:
 * - 쿼리 구성과 응답 모양 확인, `SourceFilePage` 변환.
 *
 * Does not own:
 * - Bearer 첨부 — `apiClient`. 경로 조합 · 빵부스러기 — `domain/sourceFile.ts`.
 */
import {
  apiClient,
  IntakeContractError,
  readBoolean,
  readNullableInteger,
  readNullableString,
  readRecord,
  readString,
} from "@/shared/http";
import type { SourceFile, SourceFilePage } from "../domain/sourceFile";

const RESOURCE = "datasetFile";

export type ListSourceFilesInput = {
  datasetUuid: string;
  /** root 기준 폴더 경로. 빈 문자열이면 root. */
  parent?: string;
  cursor?: string;
  /** 한 페이지 항목 수 상한(서버 최대 500). 없으면 서버 기본. */
  limit?: number;
};

export async function listSourceFiles({
  cursor,
  datasetUuid,
  limit,
  parent = "",
}: ListSourceFilesInput): Promise<SourceFilePage> {
  const response = await apiClient.get<unknown>(`/v1/datasets/${datasetUuid}/files`, {
    params: {
      parent,
      ...(cursor === undefined ? {} : { cursor }),
      ...(limit === undefined ? {} : { limit }),
    },
  });
  const body = readRecord(response.data, RESOURCE, "body");
  if (!Array.isArray(body.entries)) {
    throw new IntakeContractError(RESOURCE, "entries");
  }

  return {
    // root 는 빈 문자열이라 `readString` 을 쓸 수 없다 — 빈 값이 정상인 유일한 필드다.
    parent: typeof body.parent === "string" ? body.parent : "",
    entries: body.entries.map(parseEntry),
    cursor: readNullableString(body.cursor, RESOURCE, "cursor"),
  };
}

function parseEntry(value: unknown): SourceFile {
  const row = readRecord(value, RESOURCE, "entry");

  return {
    name: readString(row.name, RESOURCE, "name"),
    path: readString(row.path, RESOURCE, "path"),
    isDir: readBoolean(row.is_dir, RESOURCE, "is_dir"),
    sizeBytes: readNullableInteger(row.size_bytes, RESOURCE, "size_bytes"),
  };
}
